import { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";

interface ResetPasswordModalProps {
  isOpen: boolean;
  showCloseBtn: boolean;
  onClose: () => void;
  onReset: (newPassword: string) => void;
}

export default function ResetPasswordModal({
  isOpen,
  showCloseBtn = true,
  onClose,
  onReset,
}: ResetPasswordModalProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // --- Password Strength Logic ---
  const getStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strength = getStrength(newPassword);

  const isMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = strength === 4 && isMatch;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 animate-fadeIn">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 text-center">
          Reset Password
        </h2>

        {/* New Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? FaEyeSlash({}) : FaEye({})}
            </button>
          </div>

          {/* Strength Meter */}
          <div className="flex gap-1 mt-2">
            {[1, 2, 3, 4].map((level) => (
              <div
                key={level}
                className={`h-2 flex-1 rounded ${strength >= level
                  ? strength === 1
                    ? "bg-red-500"
                    : strength === 2
                      ? "bg-yellow-500"
                      : strength === 3
                        ? "bg-blue-500"
                        : "bg-green-500"
                  : "bg-gray-200"
                  }`}
              />
            ))}
          </div>
        </div>

        {/* Confirm Password */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Confirm Password
          </label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter new password"
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
          {confirmPassword.length > 0 && !isMatch && (
            <p className="text-sm text-red-500 mt-1">Passwords must match</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          {showCloseBtn && <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>}

          <button
            onClick={async () => {
              setLoading(true);
              await onReset(newPassword);
              setLoading(false);
            }}
            disabled={!canSubmit || loading}
            className={`px-5 py-2 rounded-lg text-white transition flex items-center justify-center gap-2 ${canSubmit && !loading
              ? "bg-blue-600 hover:bg-blue-700"
              : "bg-gray-400 cursor-not-allowed"
              }`}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                {/* Loading... */}
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </span>
            ) : (
              "Reset Password"
            )}

          </button>

        </div>
      </div>
    </div>
  );
}